import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  DownloadSimple, 
  ArrowLeft, 
  CheckCircle, 
  FileText,
  ChartBar,
  Table as TableIcon,
  Image as ImageIcon,
  WarningCircle,
  Lightning,
} from '@phosphor-icons/react';
import { CellRevealIcon } from '../ui/CellRevealLogo';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { deconvolutionApi, jobsApi } from '../../services/endpoints';
import type { JobResult, Job } from '../../services/types';

const COLORS = [
  'hsl(182, 72%, 28%)',  // teal-900-ish — darkest
  'hsl(182, 68%, 36%)',  // teal-800-ish
  'hsl(182, 65%, 44%)',  // teal-700-ish
  'hsl(182, 62%, 52%)',  // teal-600-ish
  'hsl(182, 60%, 60%)',  // teal-500-ish
  'hsl(170, 55%, 46%)',  // green-teal bridge
  'hsl(160, 55%, 48%)',  // soft green-teal
  'hsl(38,  70%, 52%)',  // sand accent (for variety without purple)
  'hsl(25,  65%, 58%)',  // warm sand-brown accent
];
const GRAYSCALE_COLORS = ['#9ca3af', '#6b7280', '#78716c', '#71717a', '#64748b', '#52525b', '#4b5563', '#374151'];

export function Results() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'summary' | 'samples' | 'visualizations' | 'report'>('summary');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<JobResult | null>(null);
  const [job, setJob] = useState<Job | null>(null);
  
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Stacked bar plot highlighting state
  const [highlightedCellType, setHighlightedCellType] = useState<string | null>(null);
  const [highlightColor, setHighlightColor] = useState<string>('hsl(182, 72%, 28%)');

  const fetchResults = useCallback(async () => {
    if (!jobId) return;

    try {
      setLoading(true);
      setError(null);

      const [jobResponse, resultResponse] = await Promise.all([
        jobsApi.getJob(jobId),
        deconvolutionApi.getResults(jobId)
      ]);

      setJob(jobResponse.data);
      setResult(resultResponse.data);
    } catch (err: unknown) {
      console.error('Failed to fetch results:', err);
      const detail = axios.isAxiosError(err) ? err.response?.data?.detail : undefined;
      setError(typeof detail === 'string' ? detail : 'Failed to load results. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    if (jobId) {
      fetchResults();
    }
  }, [jobId, fetchResults]);

  const handleDownload = async (format: 'csv' | 'json' | 'hdf5' | 'html' | 'markdown' = 'csv') => {
    if (!jobId) return;
    setDownloadError(null);
    try {
      await deconvolutionApi.exportResults(jobId, format);
    } catch (err: unknown) {
      console.error('Failed to download results:', err);
      setDownloadError('Export failed. Please try again.');
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  const calculateRuntime = () => {
    if (!job) return 'N/A';
    if (job.completed_at && job.started_at) {
      const start = new Date(job.started_at).getTime();
      const end = new Date(job.completed_at).getTime();
      const diffMs = end - start;
      const minutes = Math.floor(diffMs / 60000);
      const seconds = Math.floor((diffMs % 60000) / 1000);
      return `${minutes}m ${seconds}s`;
    }
    return 'N/A';
  };

  // Transform result data for charts using mean_proportions
  const cellTypeData = result?.summary?.mean_proportions
    ? Object.entries(result.summary.mean_proportions).map(([cellType, proportion]) => ({
        cellType,
        proportion: typeof proportion === 'number' ? proportion : 0,
        algorithm: result.summary.algorithm || 'unknown'
      }))
    : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <CellRevealIcon variant="light" size="xl" orbit />
            <span className="text-muted-foreground">Loading results...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !result || !job) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/jobs')}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to job queue
          </button>

          <div className="p-6 border border-border rounded-lg">
            <div className="flex items-start gap-3">
              <WarningCircle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-destructive mb-3 text-sm">{error || 'Results not found or unavailable.'}</p>
                <Button variant="secondary" onClick={fetchResults}>
                  Retry
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/jobs')}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to job queue
          </button>
          <div className="flex justify-between items-start">
            <div>
              <span className="font-mono text-[10px] tracking-[0.35em] text-muted-foreground/40 uppercase block mb-3">
                Analysis results
              </span>
              <h1 className="text-foreground mb-2">Results.</h1>
              <div className="flex items-center gap-4 text-muted-foreground flex-wrap">
                <span>Job ID: {jobId}</span>
                <span>•</span>
                <span>Completed: {formatDate(job.completed_at)}</span>
                <span>•</span>
                <span>Runtime: {calculateRuntime()}</span>
                <span>•</span>
                <span>Algorithm: {result.summary.algorithm?.toUpperCase() || 'N/A'}</span>
                <span>•</span>
                <span>Samples: {result.summary.total_samples || 0}</span>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              {downloadError && (
                <p className="text-sm text-destructive flex items-center gap-1.5">
                  <WarningCircle className="w-4 h-4 shrink-0" />
                  {downloadError}
                </p>
              )}
              <div className="flex gap-3">
                <Button variant="secondary" onClick={() => handleDownload('csv')}>
                  <DownloadSimple className="w-5 h-5 mr-2" />
                  Export CSV
                </Button>
                <Badge variant="default">Completed</Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border mb-8">
          <div className="flex gap-8">
            {(
              [
                { id: 'summary', label: 'Summary', icon: ChartBar },
                { id: 'samples', label: `Samples (${result.summary.total_samples})`, icon: TableIcon },
                { id: 'visualizations', label: 'Visualizations', icon: ImageIcon },
                { id: 'report', label: 'Report', icon: FileText }
              ] as const
            ).map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 pb-4 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-teal-600 text-teal-600 font-medium'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'summary' && (
          <div className="space-y-6">
            {/* QC Indicators */}
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-teal-500" />
                <span className="text-sm text-foreground">Deconvolution complete</span>
              </div>
              {result?.quality_metrics && (
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-teal-500" />
                  <span className="text-sm text-foreground">Quality metrics available</span>
                </div>
              )}
              {result && Object.keys(result).some(k => ['entropy', 'correlation', 'dominance'].includes(k)) && (
                <div className="flex items-center gap-2">
                  <Lightning className="w-4 h-4 text-teal-500" />
                  <span className="text-sm text-foreground">High confidence assessment</span>
                </div>
              )}
            </div>

            {/* Deconvolution Quality Metrics */}
            {result?.quality_metrics && (
              <Card className="p-6">
                <p className="font-mono text-[10px] tracking-[0.35em] text-muted-foreground/40 uppercase mb-4">
                  Quality metrics
                </p>
                <div className="grid md:grid-cols-3 gap-px bg-[#EAEAEA] border border-border rounded-xl overflow-hidden">
                  {result.quality_metrics.entropy !== undefined && (
                    <div className="p-5 bg-background">
                      <p className="font-mono text-[10px] tracking-[0.3em] text-muted-foreground/40 uppercase mb-2">Entropy</p>
                      <p className="text-2xl font-bold text-teal-600 font-display tabular-nums">
                        {(result.quality_metrics.entropy as number).toFixed(3)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">Lower = more confident</p>
                    </div>
                  )}
                  {result.quality_metrics.correlation !== undefined && (
                    <div className="p-5 bg-background">
                      <p className="font-mono text-[10px] tracking-[0.3em] text-muted-foreground/40 uppercase mb-2">Correlation</p>
                      <p className="text-2xl font-bold text-teal-600 font-display tabular-nums">
                        {(result.quality_metrics.correlation as number).toFixed(3)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">Higher = better fit</p>
                    </div>
                  )}
                  {result.quality_metrics.dominance !== undefined && (
                    <div className="p-5 bg-background">
                      <p className="font-mono text-[10px] tracking-[0.3em] text-muted-foreground/40 uppercase mb-2">Dominance</p>
                      <p className="text-2xl font-bold text-teal-600 font-display tabular-nums">
                        {((result.quality_metrics.dominance as number) * 100).toFixed(1)}%
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">Max cell type proportion</p>
                    </div>
                  )}
                </div>
              </Card>
            )}

            {/* Key Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#EAEAEA] border border-border rounded-xl overflow-hidden">
              <div className="p-5 bg-background">
                <p className="font-mono text-[10px] tracking-[0.3em] text-muted-foreground/40 uppercase mb-2">Cell types</p>
                <p className="text-3xl font-bold text-teal-600 font-display tabular-nums">{cellTypeData.length}</p>
              </div>
              <div className="p-5 bg-background">
                <p className="font-mono text-[10px] tracking-[0.3em] text-muted-foreground/40 uppercase mb-2">Dominant type</p>
                {cellTypeData.length > 0 && (
                  <>
                    <p className="text-3xl font-bold text-teal-600 font-display tabular-nums">
                      {(cellTypeData[0].proportion * 100).toFixed(1)}%
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">{cellTypeData[0].cellType}</p>
                  </>
                )}
              </div>
              <div className="p-5 bg-background">
                <p className="font-mono text-[10px] tracking-[0.3em] text-muted-foreground/40 uppercase mb-2">Algorithm</p>
                <p className="text-2xl font-bold text-teal-600 font-display">{result.summary.algorithm?.toUpperCase() || 'N/A'}</p>
              </div>
              <div className="p-5 bg-background">
                <p className="font-mono text-[10px] tracking-[0.3em] text-muted-foreground/40 uppercase mb-2">Proportions sum</p>
                <p className="text-3xl font-bold text-teal-600 font-display tabular-nums">
                  {(cellTypeData.reduce((sum, item) => sum + item.proportion, 0)).toFixed(2)}
                </p>
              </div>
            </div>

            {/* Main Chart */}
            <Card className="p-6">
              <div className="mb-6">
                <h3 className="font-semibold text-foreground">Mean Cell Type Proportions ({result.summary.algorithm?.toUpperCase() || 'N/A'})</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Average proportions across {result.summary.total_samples} samples
                </p>
              </div>
              {cellTypeData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={cellTypeData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis 
                      dataKey="cellType" 
                      angle={-45}
                      textAnchor="end"
                      height={120}
                      tick={{ fill: 'hsl(var(--muted-foreground))' }}
                    />
                    <YAxis 
                      label={{ value: 'Proportion', angle: -90, position: 'insideLeft', fill: 'hsl(var(--muted-foreground))' }}
                      tick={{ fill: 'hsl(var(--muted-foreground))' }}
                    />
                    <RechartsTooltip 
                      contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', color: 'hsl(var(--foreground))' }}
                    />
                    <Bar dataKey="proportion" fill="hsl(201, 96%, 32%)" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-64 text-muted-foreground">
                  No cell type data available
                </div>
              )}
            </Card>

            {/* Pie Chart */}
            <Card className="p-6">
              <h3 className="font-semibold text-foreground mb-6">Proportional Distribution</h3>
              {cellTypeData.length > 0 ? (
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart>
                    <Pie
                      data={cellTypeData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ payload }) => `${payload.cellType}: ${(payload.proportion * 100).toFixed(1)}%`}
                      outerRadius={120}
                      fill="hsl(182, 72%, 28%)"
                      dataKey="proportion"
                    >
                      {cellTypeData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip 
                      contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', color: 'hsl(var(--foreground))' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-64 text-muted-foreground">
                  No cell type data available
                </div>
              )}
            </Card>
          </div>
        )}

        {activeTab === 'samples' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <p className="text-muted-foreground">
                Per-sample cell type proportions ({result.samples.length} samples shown, page {result.pagination?.page || 1} of {result.pagination?.total_pages || 1})
              </p>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={() => handleDownload('csv')}>
                  <DownloadSimple className="w-5 h-5 mr-2" />
                  DownloadSimple All Samples
                </Button>
              </div>
            </div>

            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted border-b border-border">
                    <tr>
                      <th className="px-6 py-4 text-left text-foreground font-semibold">Sample Name</th>
                      {cellTypeData.length > 0 && cellTypeData.map((ct, idx) => (
                        <th key={idx} className="px-4 py-4 text-right text-foreground text-sm font-semibold">{ct.cellType}</th>
                      ))}
                      <th className="px-6 py-4 text-right text-foreground font-semibold">Quality</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {result.samples.map((sample, idx) => (
                      <tr key={idx} className="hover:bg-muted/50">
                        <td className="px-6 py-4 text-foreground font-medium">{sample.sample_name}</td>
                        {cellTypeData.map((ct, ctIdx) => {
                          const proportion = sample.cell_type_proportions[ct.cellType] || 0;
                          return (
                            <td key={ctIdx} className="px-4 py-4 text-right text-muted-foreground text-sm">
                              {(proportion * 100).toFixed(1)}%
                            </td>
                          );
                        })}
                        <td className="px-6 py-4 text-right text-muted-foreground">
                          {sample.quality_metrics?.correlation 
                            ? `r=${sample.quality_metrics.correlation.toFixed(2)}`
                            : 'N/A'
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {/* Pagination Controls */}
            {result.pagination && result.pagination.total_pages > 1 && (
              <div className="flex justify-center items-center gap-4">
                <Button
                  variant="secondary"
                  disabled={result.pagination.page === 1}
                  onClick={() => {
                    console.log('Previous page');
                  }}
                >
                  Previous
                </Button>
                <span className="text-muted-foreground">
                  Page {result.pagination.page} of {result.pagination.total_pages}
                </span>
                <Button
                  variant="secondary"
                  disabled={result.pagination.page === result.pagination.total_pages}
                  onClick={() => {
                    console.log('Next page');
                  }}
                >
                  Next
                </Button>
              </div>
            )}

            <div className="pt-4 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Each row shows cell type proportions for an individual sample. Mean proportions are shown in the Summary tab. Quality correlation indicates algorithm performance per sample.
              </p>
            </div>
          </div>
        )}

        {activeTab === 'visualizations' && (
          <div className="space-y-6">
            {/* Stacked Bar Chart - Per-Sample Composition */}
            <Card className="p-6">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="font-semibold text-foreground">Per-Sample Cell Type Composition</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Stacked view showing cell type distribution across samples
                  </p>
                </div>
                <Button variant="secondary">
                  <DownloadSimple className="w-5 h-5 mr-2" />
                  Export as PNG
                </Button>
              </div>
              
              {/* Highlighting Controls */}
              <div className="mb-6 p-4 border border-border rounded-lg">
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Highlight Cell Type
                    </label>
                    <select
                      value={highlightedCellType || ''}
                      onChange={(e) => setHighlightedCellType(e.target.value || null)}
                      className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-lg focus:outline-hidden focus:ring-2 focus:ring-ring"
                    >
                      <option value="">None (show all colors)</option>
                      {cellTypeData.map((ct) => (
                        <option key={ct.cellType} value={ct.cellType}>
                          {ct.cellType}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {highlightedCellType && (
                    <div className="flex-shrink-0">
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Highlight Color
                      </label>
                      <div className="flex gap-2 items-center">
                        <input
                          type="color"
                          value={highlightColor}
                          onChange={(e) => setHighlightColor(e.target.value)}
                          className="h-10 w-20 rounded border border-input cursor-pointer"
                        />
                        <div className="flex gap-1">
                          {['hsl(201, 96%, 32%)', '#3b82f6', '#ec4899', '#f59e0b', '#10b981', '#8b5cf6'].map((color) => (
                            <button
                              key={color}
                              onClick={() => setHighlightColor(color)}
                              className="w-8 h-8 rounded border-2 border-border hover:border-foreground transition-colors"
                              style={{ backgroundColor: color }}
                              title={`Use ${color}`}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {result.samples.length > 0 ? (
                <ResponsiveContainer width="100%" height={500}>
                  <BarChart 
                    data={(() => {
                      const chartData = result.samples.map(sample => ({
                        sample: sample.sample_name,
                        ...sample.cell_type_proportions
                      } as Record<string, string | number>));
                      
                      if (highlightedCellType) {
                        return chartData.sort((a, b) => {
                          const aValue = (a[highlightedCellType] as number) || 0;
                          const bValue = (b[highlightedCellType] as number) || 0;
                          return bValue - aValue;
                        });
                      }
                      
                      return chartData;
                    })()}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis type="number" domain={[0, 1]} tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                    <YAxis 
                      type="category" 
                      dataKey="sample" 
                      width={100}
                      tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                    />
                    <RechartsTooltip 
                      contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', color: 'hsl(var(--foreground))' }}
                      formatter={(value) => `${(Number(value) * 100).toFixed(1)}%`}
                      labelStyle={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px', color: 'hsl(var(--foreground))' }}
                    />
                    <Legend 
                      wrapperStyle={{ color: 'hsl(var(--foreground))' }}
                      iconType="square"
                    />
                    {highlightedCellType && (
                      <Bar 
                        key={`highlighted-${highlightedCellType}`}
                        dataKey={highlightedCellType} 
                        stackId="a" 
                        fill={highlightColor}
                        name={`${highlightedCellType} (highlighted)`}
                      />
                    )}
                    {cellTypeData.map((ct, idx) => {
                      if (ct.cellType === highlightedCellType) return null;
                      return (
                        <Bar 
                          key={ct.cellType}
                          dataKey={ct.cellType} 
                          stackId="a" 
                          fill={highlightedCellType ? GRAYSCALE_COLORS[idx % GRAYSCALE_COLORS.length] : COLORS[idx % COLORS.length]}
                        />
                      );
                    })}
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-64 text-muted-foreground">
                  No sample data available
                </div>
              )}
            </Card>

            {/* Heatmap - Cell Type Proportions Across Samples */}
            <Card className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="font-semibold text-foreground">Cell Type Heatmap</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Color-coded proportions across all samples (darker = higher proportion)
                  </p>
                </div>
                <Button variant="secondary">
                  <DownloadSimple className="w-5 h-5 mr-2" />
                  Export as PNG
                </Button>
              </div>
              {result.samples.length > 0 && cellTypeData.length > 0 ? (
                <div className="overflow-x-auto">
                  <div className="inline-block min-w-full">
                    {/* Heatmap Header */}
                    <div className="flex border-b border-border">
                      <div className="w-32 p-2 font-semibold text-sm text-foreground flex-shrink-0">
                        Sample
                      </div>
                      {cellTypeData.map((ct, idx) => (
                        <div 
                          key={idx} 
                          className="flex-1 min-w-[80px] p-2 font-semibold text-xs text-foreground text-center"
                        >
                          {ct.cellType}
                        </div>
                      ))}
                    </div>
                    
                    {/* Heatmap Rows */}
                    <div className="max-h-[600px] overflow-y-auto">
                      {result.samples.map((sample, sampleIdx) => (
                        <div key={sampleIdx} className="flex border-b border-border/50 hover:bg-muted/30">
                          <div className="w-32 p-2 text-sm text-foreground flex-shrink-0">
                            {sample.sample_name}
                          </div>
                          {cellTypeData.map((ct, ctIdx) => {
                            const proportion = sample.cell_type_proportions[ct.cellType] || 0;
                            const backgroundColor = `hsla(201, 96%, 32%, ${proportion})`;
                            
                            return (
                              <div 
                                key={ctIdx}
                                className="flex-1 min-w-[80px] p-2 text-center text-xs transition-all hover:ring-2 hover:ring-primary"
                                style={{ backgroundColor }}
                                title={`${ct.cellType}: ${(proportion * 100).toFixed(1)}%`}
                              >
                                <span className={proportion > 0.5 ? 'text-white' : 'text-foreground'}>
                                  {(proportion * 100).toFixed(1)}%
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                    
                    {/* Color Legend */}
                    <div className="mt-4 p-4 bg-muted rounded-lg">
                      <p className="text-sm font-semibold text-foreground mb-2">Color Scale</p>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">0%</span>
                        <div className="flex-1 h-6 rounded" style={{
                          background: 'linear-gradient(to right, hsla(201, 96%, 32%, 0), hsl(201, 96%, 32%))'
                        }} />
                        <span className="text-xs text-muted-foreground">100%</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 text-muted-foreground">
                  No sample data available for heatmap
                </div>
              )}
            </Card>

            {/* Original Cell Type Distribution */}
            <Card className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-semibold text-foreground">Mean Cell Type Distribution</h3>
                <Button variant="secondary">
                  <DownloadSimple className="w-5 h-5 mr-2" />
                  Export as PNG
                </Button>
              </div>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={cellTypeData.slice(0, 6)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis 
                    dataKey="cellType" 
                    angle={-45}
                    textAnchor="end"
                    height={120}
                    tick={{ fill: 'hsl(var(--muted-foreground))' }}
                  />
                  <YAxis tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                  <RechartsTooltip 
                    contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px', color: 'hsl(var(--foreground))' }}
                  />
                  <Legend />
                  <Bar dataKey="proportion" fill="hsl(201, 96%, 32%)" name="Proportion" />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>
        )}

        {activeTab === 'report' && (
          <div className="space-y-6">
            <Card className="p-8">
              <div className="flex items-start gap-4 mb-8">
                <div className="w-9 h-9 bg-teal-50 border border-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4 text-teal-600" />
                </div>
                <div>
                  <p className="font-mono text-[10px] tracking-[0.35em] text-muted-foreground/40 uppercase mb-1">
                    Export report
                  </p>
                  <h2 className="text-foreground mb-1">Analysis report</h2>
                  <p className="text-muted-foreground text-sm">
                    Full results, visualizations, and quality metrics in a shareable format
                  </p>
                </div>
              </div>

              <div className="border border-border rounded-lg mb-6">
                <p className="px-5 pt-4 pb-2 font-mono text-[10px] tracking-[0.35em] text-muted-foreground/40 uppercase">
                  Report contents
                </p>
                <div className="divide-y divide-border">
                  {[
                    'Executive summary with key findings',
                    'Quality control metrics and validation results',
                    'Cell type composition tables (all algorithms)',
                    'High-resolution visualizations',
                    'Statistical analysis and confidence intervals',
                    'Methodology and parameters used',
                  ].map(item => (
                    <div key={item} className="flex items-center gap-3 px-5 py-3">
                      <CheckCircle className="w-4 h-4 text-teal-500 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={() => handleDownload('html')}>
                  <DownloadSimple className="w-4 h-4 mr-2" />
                  DownloadSimple HTML report
                </Button>
                <Button variant="secondary" onClick={() => handleDownload('markdown')}>
                  <DownloadSimple className="w-4 h-4 mr-2" />
                  DownloadSimple Markdown
                </Button>
              </div>

              <p className="text-xs text-muted-foreground mt-4 pt-4 border-t border-border">
                HTML provides an interactive experience. Markdown is ideal for integrating into other documents or notebooks.
              </p>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
